import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import {
  getPartnerSignupType,
} from 'common/partnerHelpers';

import {
  getHelpCardTitle,
} from 'common/helpCardHelpers';

import {
  SCREEN_ID_PARTNER_SETTINGS,
} from 'client/constants/screenIds';

import {
  hasRoleAdmin,
  USER_ROLE_ADMIN,
  USER_ROLE_PARTNER,
} from 'common/userRoles';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrNavigationButton from 'client/components/SrNavigationButton/SrNavigationButton';
import SrHelpCardSection from 'client/components/SrHelpCardSection/SrHelpCardSection';

import PartnerActions from 'client/actions/Partner';
import HelpCardActions from 'client/actions/HelpCard';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, {
  getUserLandingPage,
} from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
    USER_ROLE_PARTNER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.helpCards = [
      'helpCards',
      'list',
      [SCREEN_ID_PARTNER_SETTINGS, getPartnerSignupType(partner)].join('-'),
    ];
  }

  return cursors;
})
class PartnerSettingsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
    section: 'Partner',
  })

  static fetchData(match, inject) {

    const promises = [
      PartnerActions.loadPartnerBySlug(inject, {
        slug: match.params.partnerSlug,
      }),
    ];

    return Promise.all(promises).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);

      return HelpCardActions.getList(inject, {
        screenId: SCREEN_ID_PARTNER_SETTINGS,
        partnerSignupType: getPartnerSignupType(partner),
      });
    });
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object,
    isTransitioning: PropTypes.bool,
    helpCards: PropTypes.array,
  }

  render() {
    const {
      partner,
      currentUser,
      isTransitioning,
      helpCards,
    } = this.props;

    if (isTransitioning && !partner) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return (
        <Redirect to={userLandingPage} />
      );
    }

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}`}
        navbarTitle={partner.displayName}
        pageHeader='Account Settings'
        pageSubtitle='Set the criteria that get used by default for all of your listings.'
      >
        <SrNavigationButton
          color='#007AFF'
          iconUrl='/images/house-icon-white.svg'
          to={`/partner/${partner.slug}/settings/listing`}
          title='Listing &amp; Marketing'
          subtitle='Co-Listing Agent'
        />
        <SrBox marginTop={10} />
        <SrNavigationButton
          color='#AD00AD'
          iconUrl='/images/checkmark-white-bold.svg'
          iconSize={24}
          to={`/partner/${partner.slug}/settings/applications`}
          title='Renter &amp; Pet Criteria'
          subtitle='Credit Score, Pet Fees'
        />
        <SrBox marginTop={10} />
        <SrNavigationButton
          color='#00A238'
          iconUrl='/images/dollar-white.svg'
          to={`/partner/${partner.slug}/settings/banks`}
          title='Security Deposits'
          subtitle='Setup collection method'
        />
        {hasRoleAdmin(currentUser.roles) && (
          <>
            <SrBox marginTop={10} />
            <SrNavigationButton
              color='#007AFF'
              iconUrl='/images/house-icon-white.svg'
              to={`/partner/${partner.slug}/settings/approval-flow`}
              title='Approval Flow'
              subtitle='Configuration'
            />
          </>
        )}
        {hasRoleAdmin(currentUser.roles) && (
          <>
            <SrBox marginTop={10} />
            <SrNavigationButton
              color='#FFCD00'
              iconUrl='/images/sr-icon-white.svg'
              iconSize='32px'
              to={`/partner/${partner.slug}/settings/admin`}
              title='Admin Settings'
              subtitle='Only shown to admins'
            />
          </>
        )}
        {helpCards && helpCards.length > 0 && (
          <SrBox marginTop='80px' padding='8px'>
            <SrHelpCardSection
              helpCards={helpCards}
              title={getHelpCardTitle({ partner })}
            />
          </SrBox>
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerSettingsPage.displayName = 'PartnerSettingsPage';

export default PartnerSettingsPage;
